import classNames from 'classnames';
import React, { FormEvent, useEffect, useRef } from 'react';
import Linkify from 'react-linkify';

import { convertHtmlToText } from 'src/components/Chat/utils/convertHtmlToText';

export interface Props {
  editable?: boolean;
  isSender?: boolean;
  content: string;
  onChange?: (editedMessage: string) => void;
}

export const ChatTextContent: React.FC<Props> = ({
  editable = false,
  isSender = false,
  content,
  onChange,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editable && textRef.current) {
      textRef.current.focus();
    }
  }, [editable, textRef]);

  const getLinkifyComponentDecorator = (
    decoratedHref: string,
    decoratedText: string,
    key: number,
  ) => (
    <a
      href={decoratedHref}
      key={key}
      target="_blank"
      rel="noopener noreferrer"
      className={classNames('font-semibold', {
        'text-white': isSender,
        'text-blue-200': !isSender,
      })}
    >
      {decoratedText}
    </a>
  );

  const handleInput = (event: FormEvent<HTMLDivElement>) => {
    if (editable && onChange) {
      // clean input
      const text = convertHtmlToText(event.currentTarget.innerHTML);
      onChange(text);
    }
  };

  return (
    <div
      className={classNames(
        'flex-grow shadow p-4 rounded-t-lg font-medium whitespace-pre-wrap w-full break-words',
        {
          'rounded-r-lg ml-4 bg-white text-gray-500': !isSender,
          'rounded-l-lg mr-4': isSender,
          'bg-blue-200 text-white': isSender && !editable,
          'bg-white text-gray-850': isSender && editable,
        },
      )}
      ref={textRef}
      contentEditable={editable}
      suppressContentEditableWarning
      onInput={handleInput}
    >
      {editable ? (
        content
      ) : (
        <Linkify componentDecorator={getLinkifyComponentDecorator}>{content}</Linkify>
      )}
    </div>
  );
};
